import { createIdempotencyKey } from "@lafayette/api-client";
import { STAFF_CATALOG_BOUNDS } from "@lafayette/schemas";
import { useMemo, useRef, useState } from "react";
import { NameWord, opaqueKey } from "../../ui/accessible-name";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { TextField } from "../../ui/fields";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { RadioInput, RadioRow } from "../../ui/radio";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { describeOutcome } from "../commands";
import { LiveRegion, MoveButtons, useAnnouncer, useMoveFocus } from "../components/reorder";
import { hintText, listRow, plainList } from "../components/styles";
import { describeCatalogError } from "../errors";
import {
  appendMedia,
  MEDIA_ERROR_SEPARATOR,
  type MediaDraft,
  mediaDraftsFromDetail,
  mediaToBody,
  moveItem,
  removeMedia,
  setCoverMedia,
  updateMedia,
  validateMedia,
} from "../form-mappers";
import { useCatalogRuntime } from "../runtime";
import {
  describeUpload,
  retryStage,
  startUpload,
  type UploadEvent,
  type UploadStage,
  type UploadState,
  uploadReducer,
} from "../upload-machine";
import { type ItemSectionContext, useSectionState } from "./section-support";

const ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_MEDIA = STAFF_CATALOG_BOUNDS.media;

/** The file name of a storage path: what a staff member recognises. */
function fileNameOf(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** A DOM id for a field of one row. The key of the path is the row's stable key, so the id survives a reorder. */
function fieldId(path: string, field: string): string {
  // The path holds the file name, so the id holds a key made from it, not the path.
  return `media-${opaqueKey(path)}-${field}`;
}

type DriveInput = Readonly<{ contentType: string; byteSize: number; path: string | null }>;

/**
 * Images of the item: list, reorder, remove, alt text, cover, and upload. An upload goes browser to
 * storage on a signed URL, then is attached by a media save that the server checks. A failed upload or
 * a failed check adds nothing to the list. Rows are keyed by `storageObjectPath` (the stable reference).
 */
export function MediaManager({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command } = ctx;
  const { api, uploadFile } = useCatalogRuntime();
  const server = useMemo(() => mediaDraftsFromDetail(detail), [detail]);
  const section = useSectionState<readonly MediaDraft[]>("media", server);
  const list = section.values;
  const live = useAnnouncer();
  const focus = useMoveFocus<HTMLOListElement>();
  const [uploads, setUploads] = useState<readonly UploadState[]>([]);

  // The upload runs for seconds. It reads the newest detail when it attaches, not the one it started with.
  const latest = useRef(detail);
  latest.current = detail;
  const files = useRef(new Map<string, File>());
  const queue = useRef<Promise<void>>(Promise.resolve());
  const counter = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const previews = useMemo(
    () => new Map(detail.media.map((entry) => [entry.record.storageObjectPath, entry.previewUrl])),
    [detail.media],
  );

  function update(id: string, event: UploadEvent) {
    setUploads((current) =>
      current.map((entry) => (entry.id === id ? uploadReducer(entry, event) : entry)),
    );
  }

  function fail(id: string, name: string, message: string, retryable: boolean) {
    update(id, { type: "failed", message, retryable });
    live.announce(`${name} could not be added. ${message}`);
  }

  async function drive(id: string, stage: UploadStage, input: DriveInput) {
    const file = files.current.get(id);
    if (file === undefined) return;
    const name = file.name;
    let path = input.path;

    if (stage === "requesting") {
      let target: Awaited<ReturnType<typeof api.createMediaUploadTarget>>;
      try {
        target = await api.createMediaUploadTarget(
          detail.item.id,
          {
            contentType: input.contentType as "image/png",
            byteSize: input.byteSize,
          },
          { idempotencyKey: createIdempotencyKey() },
        );
      } catch (error) {
        const failure = describeCatalogError(error);
        fail(
          id,
          name,
          failure.kind === "unavailable"
            ? "Media storage is not available right now. Try again later."
            : failure.message,
          failure.retryable,
        );
        return;
      }
      path = target.storageObjectPath;
      update(id, { type: "target_received", storageObjectPath: path });
      live.announce(`Uploading ${name}.`);
      try {
        await uploadFile(target, file);
      } catch {
        fail(
          id,
          name,
          "The file could not be sent to storage. Check the connection and try again.",
          true,
        );
        return;
      }
      update(id, { type: "uploaded" });
    }

    if (path === null) return;
    const current = latest.current;
    const outcome = await command.run("replaceMedia", {
      expectedVersion: current.item.version,
      media: mediaToBody(appendMedia(mediaDraftsFromDetail(current), path)),
    });
    if (outcome.ok) {
      update(id, { type: "attached" });
      files.current.delete(id);
      setUploads((entries) => entries.filter((entry) => entry.id !== id));
      // Local edits stay as they are; the new image joins the draft list.
      const attached = path;
      section.setValues((currentList) => appendMedia(currentList, attached));
      live.announce(`${name} added.`);
      return;
    }
    if (outcome.failure.kind === "invalid") {
      fail(
        id,
        name,
        "The server could not accept this file, so it was not added. Use a valid JPEG, PNG or WebP image.",
        false,
      );
    } else if (outcome.conflict?.changedElsewhere) {
      fail(id, name, "The item was changed elsewhere. Try adding the file again.", true);
    } else {
      fail(id, name, outcome.failure.message, outcome.failure.retryable);
    }
  }

  function enqueue(id: string, stage: UploadStage, input: DriveInput) {
    queue.current = queue.current.then(() => drive(id, stage, input));
  }

  function onFiles(selected: FileList | null) {
    if (selected === null) return;
    for (const file of Array.from(selected)) {
      counter.current += 1;
      const id = `upload-${counter.current}`;
      const state = startUpload(id, { name: file.name, type: file.type, size: file.size });
      files.current.set(id, file);
      setUploads((entries) => [...entries, state]);
      if (state.status === "requesting") {
        enqueue(id, "requesting", {
          contentType: state.contentType,
          byteSize: file.size,
          path: null,
        });
      } else if (state.status === "failed") {
        live.announce(`${file.name} was not added. ${state.message}`);
      }
    }
  }

  function retry(state: UploadState) {
    const stage = retryStage(state);
    if (stage === null || state.status !== "failed" || state.contentType === null) return;
    update(state.id, { type: "retry" });
    enqueue(state.id, stage, {
      contentType: state.contentType,
      byteSize: state.byteSize,
      path: stage === "verifying" ? state.storageObjectPath : null,
    });
  }

  function dismiss(id: string) {
    files.current.delete(id);
    setUploads((entries) => entries.filter((entry) => entry.id !== id));
  }

  function move(index: number, delta: -1 | 1) {
    const entry = list[index];
    if (entry === undefined) return;
    focus.requestFocus(opaqueKey(entry.storageObjectPath), delta === -1 ? "up" : "down");
    section.setValues((current) => moveItem(current, index, delta));
    live.announce(
      `Moved ${fileNameOf(entry.storageObjectPath)} to position ${index + delta + 1} of ${list.length}.`,
    );
  }

  async function submit() {
    const errors = validateMedia(list);
    if (Object.keys(errors).length > 0) {
      section.reject(errors);
      return;
    }
    section.clearErrors();
    const outcome = await command.run("replaceMedia", {
      expectedVersion: detail.item.version,
      media: mediaToBody(list),
    });
    section.setMessage(describeOutcome(outcome, "Media saved."));
    if (outcome.ok) section.reset(mediaDraftsFromDetail(outcome.detail));
  }

  const errorFields = list.flatMap((entry) =>
    (["altText", "caption", "credit"] as const)
      .filter(
        (field) =>
          section.errors[`${entry.storageObjectPath}${MEDIA_ERROR_SEPARATOR}${field}`] !==
          undefined,
      )
      .map((field) => ({
        id: fieldId(entry.storageObjectPath, field),
        label: `${field === "altText" ? "Alt text" : field === "caption" ? "Caption" : "Credit"} for ${fileNameOf(entry.storageObjectPath)}`,
      })),
  );

  const uploading = uploads.some((entry) => entry.status !== "failed");

  return (
    <SectionCard
      id="media"
      title="Images"
      description="The first image marked as cover is the main picture. Every image needs alt text for people who cannot see it."
    >
      <SectionForm
        label="Images"
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={errorFields}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
      >
        <p className={hintText}>
          {list.length} of {MAX_MEDIA} images.
        </p>
        {list.length === 0 ? (
          <p className={hintText}>No images yet.</p>
        ) : (
          <ol
            className={cn(plainList, "gap-4")}
            ref={focus.container}
            aria-label="Images in page order"
          >
            {list.map((entry, index) => {
              const name = fileNameOf(entry.storageObjectPath);
              const error = (field: string) =>
                section.errors[`${entry.storageObjectPath}${MEDIA_ERROR_SEPARATOR}${field}`];
              const previewUrl = previews.get(entry.storageObjectPath) ?? null;
              const change = (patch: Partial<Pick<MediaDraft, "altText" | "caption" | "credit">>) =>
                section.setValues((current) =>
                  updateMedia(current, entry.storageObjectPath, patch),
                );
              return (
                <li
                  key={entry.storageObjectPath}
                  data-row-key={opaqueKey(entry.storageObjectPath)}
                  className="grid gap-4 rounded-md border border-border p-4 sm:grid-cols-[10rem_minmax(0,1fr)_auto]"
                >
                  <div className="grid min-h-30 place-items-center overflow-hidden rounded-sm bg-muted">
                    {/* `alt` is the one attribute that holds staff-written text on purpose: it is the alternative text of a catalog image,
                        written to be read in place of the image. (The sentinel tests name this exception.) */}
                    {previewUrl !== null ? (
                      <img
                        src={previewUrl}
                        alt={entry.altText}
                        className="size-full max-h-50 object-cover"
                      />
                    ) : (
                      <span className="text-sm text-muted-foreground">No preview</span>
                    )}
                  </div>
                  <div className="grid min-w-0 content-start gap-3">
                    <p className="m-0 flex flex-wrap items-center gap-2 font-semibold">
                      <span id={`media-name-${index}`}>{name}</span>
                      {entry.isCover ? <Badge tone="brass">Cover</Badge> : null}
                    </p>
                    <TextField
                      id={fieldId(entry.storageObjectPath, "altText")}
                      label={`Alt text for ${name}`}
                      value={entry.altText}
                      onChange={(altText) => change({ altText })}
                      error={error("altText")}
                      disabled={readOnly}
                      maxLength={300}
                    />
                    <TextField
                      id={fieldId(entry.storageObjectPath, "caption")}
                      label={`Caption for ${name}`}
                      value={entry.caption}
                      onChange={(caption) => change({ caption })}
                      error={error("caption")}
                      disabled={readOnly}
                      maxLength={300}
                    />
                    <TextField
                      id={fieldId(entry.storageObjectPath, "credit")}
                      label={`Credit for ${name}`}
                      value={entry.credit}
                      onChange={(credit) => change({ credit })}
                      error={error("credit")}
                      disabled={readOnly}
                      maxLength={200}
                    />
                    <RadioRow>
                      <RadioInput
                        name="media-cover"
                        aria-labelledby={`media-cover-${index} media-name-${index}`}
                        checked={entry.isCover}
                        disabled={readOnly}
                        onChange={() => {
                          section.setValues((current) =>
                            setCoverMedia(current, entry.storageObjectPath),
                          );
                          live.announce(`${name} is now the cover image.`);
                        }}
                      />
                      Cover image
                    </RadioRow>
                    <NameWord id={`media-cover-${index}`}>Cover image:</NameWord>
                  </div>
                  {readOnly ? null : (
                    <div className="flex flex-row flex-wrap items-start gap-2 sm:flex-col sm:items-stretch">
                      <MoveButtons
                        nameId={`media-name-${index}`}
                        index={index}
                        count={list.length}
                        onMove={(delta) => move(index, delta)}
                      />
                      <NameWord id={`media-remove-${index}`}>Remove</NameWord>
                      <Button
                        type="button"
                        variant="secondary"
                        aria-labelledby={`media-remove-${index} media-name-${index}`}
                        onClick={() => {
                          section.setValues((current) =>
                            removeMedia(current, entry.storageObjectPath),
                          );
                          live.announce(`Removed ${name}. Save to keep this change.`);
                          fileInput.current?.focus();
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {readOnly ? null : (
          <div className="grid gap-2">
            <div className="grid min-w-0 content-start gap-1.5">
              <Label htmlFor="media-upload-input">Add images</Label>
              <p id="media-upload-hint" className={hintText}>
                JPEG, PNG or WebP, up to 10 MB each. An image is added as soon as it is uploaded and
                checked.
              </p>
              <Input
                id="media-upload-input"
                className="h-auto py-2.5 compact:h-auto compact:py-1"
                ref={fileInput}
                type="file"
                accept={ACCEPT}
                multiple
                aria-describedby="media-upload-hint"
                disabled={list.length >= MAX_MEDIA}
                onChange={(event) => {
                  onFiles(event.currentTarget.files);
                  event.currentTarget.value = "";
                }}
              />
            </div>
            {uploads.length > 0 ? (
              <ul className={plainList} aria-label="Uploads">
                {uploads.map((entry) => (
                  <li
                    key={entry.id}
                    className={cn(
                      listRow,
                      "flex flex-wrap items-center gap-x-4 gap-y-2",
                      entry.status === "failed" && "border-destructive-border bg-destructive-soft",
                    )}
                  >
                    <span className="font-semibold">{entry.fileName}</span>
                    <span>
                      <span aria-hidden="true">{entry.status === "failed" ? "⚠ " : "… "}</span>
                      {describeUpload(entry)}
                    </span>
                    {entry.status === "failed" ? (
                      <span className="flex flex-wrap gap-2">
                        {retryStage(entry) !== null ? (
                          <Button type="button" variant="secondary" onClick={() => retry(entry)}>
                            Retry {entry.fileName}
                          </Button>
                        ) : null}
                        <Button type="button" variant="secondary" onClick={() => dismiss(entry.id)}>
                          Dismiss {entry.fileName}
                        </Button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {uploading ? <p className={hintText}>Uploading. Keep this page open.</p> : null}
          </div>
        )}
        <LiveRegion message={live.message} />
      </SectionForm>
    </SectionCard>
  );
}
